import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, History, FileText, Mail, Database } from 'lucide-react';
import CSVProcessingLoader from './CSVProcessingSpinner';
import '../../styles/reportingPage.css'; // Import the CSS file
import Button from '../../ui-components/Button/Button';
import 'ui-library-aol/dist/style.css';
import {
  updateUserActivity
} from '../access/accessControlUtils';
  
const ReportingPage = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 769);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 769 && window.innerWidth < 1024);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // SEPARATE DATA LOADING FROM ANIMATION DISPLAY
  const [dataLoading, setDataLoading] = useState(true);
  const [showAnimation, setShowAnimation] = useState(true);
  
  const navigate = useNavigate();

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 769);
      setIsTablet(width >= 769 && width < 1024);

      // Auto-collapse sidebar on small screens
      if (width < 1024) {
        setSidebarCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once on mount

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Simulate data loading
  useEffect(() => {
    const loadReportingData = async () => {
      try {
        setDataLoading(true);
        
        // Simulate API calls for reporting data
        await new Promise(resolve => setTimeout(resolve, 2000));
        
      } catch (error) {
        console.error('Error loading reporting data:', error);
      } finally {
        setDataLoading(false);
      }
    };

    loadReportingData();
  }, []);

  // Navigation function
  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleMenuItemClick = (item) => {
    // console.log(`Menu item clicked: ${item}`);
  };

  const reportingFeatures = [
    {
      id: 'csv-download',
      title: 'CSV Export',
      description: 'Download your data as CSV files for offline analysis and reporting.',
      icon: <Download className="w-8 h-8" />,
      path: '/csv/download',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      features: ['Instant download', 'Multiple formats', 'Custom date ranges']
    },
    {
      id: 'csv-scheduler',
      title: 'Scheduled CSV Delivery',
      description: 'Schedule automated CSV reports to be delivered via email on a regular basis.',
      icon: <Mail className="w-8 h-8" />,
      path: '/csv/scheduler',
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600',
      features: ['Email delivery', 'Custom schedules', 'Multiple recipients']
    },
    {
      id: 'datalake-scheduler',
      title: 'Data Lake Push Scheduler',
      description: 'Configure automated data transfers to your cloud data lake infrastructure.',
      icon: <Database className="w-8 h-8" />,
      path: '/cloud/scheduler',
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600',
      features: ['Cloud integration', 'Automated transfers', 'Data validation']
    },
    {
      id: 'transfer-history',
      title: 'Transfer History',
      description: 'View and monitor the history of all your data transfers and exports.',
      icon: <History className="w-8 h-8" />,
      path: '/cloud/transfer-history',
      color: 'bg-orange-500',
      hoverColor: 'hover:bg-orange-600',
      features: ['Complete audit trail', 'Status monitoring', 'Error tracking']
    }
  ];

  // Helper function to get responsive classes
  const getResponsiveClass = (baseClass, mobileClass = '', tabletClass = '', desktopClass = '') => {
    let classes = [baseClass];
    if (isMobile && mobileClass) classes.push(mobileClass);
    else if (isTablet && tabletClass) classes.push(tabletClass);
    else if (!isMobile && !isTablet && desktopClass) classes.push(desktopClass);
    return classes.join(' ');
  };

  // Show animation until BOTH data is loaded AND animation is complete
  if (showAnimation || dataLoading) {
    return (
      <>
        {/* CSV Processing Loader */}
        <CSVProcessingLoader 
          onComplete={() => setShowAnimation(false)}
          duration={1000} // 2 second animation - you can adjust this
        />
        
        {/* Content Below Spinner - Blurred and Dimmed */}
        <div className="reporting-loading-overlay">
          <div className="reporting-flex-container">
            {/* Sidebar - Hidden on mobile during loading */}
            {!isMobile}
            
            {/* Main Content */}
            <div className={`reporting-main-content ${isMobile ? 'mobile' : ''}`}>
              <div className={getResponsiveClass('reporting-page-content', 'mobile', 'tablet')}>
                <div className="reporting-content-wrapper">
                  {/* Page Header */}
                  <div className={getResponsiveClass('reporting-page-header', 'mobile')}>
                    <div className={getResponsiveClass('reporting-header-flex', 'mobile')}>
                      <FileText className={getResponsiveClass('reporting-header-icon', 'mobile')} />
                      <h1 className={getResponsiveClass('reporting-page-title', 'mobile', 'tablet')}>
                        Reporting Dashboard
                      </h1>
                    </div>
                    <p className={getResponsiveClass('reporting-page-description', 'mobile')}>
                      Export data, schedule deliveries, and manage your data transfer workflows
                    </p>
                  </div>

                  {/* Feature Cards Grid - Loading State */}
                  <div className={getResponsiveClass('reporting-features-grid', 'mobile', 'tablet', 'desktop')}>
                    {reportingFeatures.map((feature) => (
                      <div
                        key={feature.id}
                        className="reporting-feature-card loading"
                      >
                        <div className={getResponsiveClass('reporting-feature-card-content', 'mobile')}>
                          {/* Icon and Title */}
                          <div className={getResponsiveClass('reporting-feature-header', 'mobile')}>
                            <div className={`reporting-feature-icon-wrapper ${feature.id} ${isMobile ? 'mobile' : ''}`}>
                              {React.cloneElement(feature.icon, { 
                                className: isMobile ? "w-6 h-6" : "w-8 h-8" 
                              })}
                            </div>
                            <div>
                              <h3 className={getResponsiveClass('reporting-feature-title', 'mobile')}>
                                {feature.title}
                              </h3>
                            </div>
                          </div>

                          {/* Description */}
                          <p className={getResponsiveClass('reporting-feature-description', 'mobile')}>
                            {feature.description}
                          </p>

                          {/* Features List */}
                          <div className="reporting-features-list-wrapper">
                            <ul className="reporting-features-list">
                              {feature.features.map((featureItem, index) => (
                                <li key={index} className={getResponsiveClass('reporting-feature-item', 'mobile')}>
                                  <div className="reporting-feature-bullet"></div>
                                  <span>{featureItem}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Action Button - Disabled during loading */}
                          <div className="reporting-action-button-wrapper">
                            <button
                              disabled
                              className={`reporting-action-button ${feature.id} ${isMobile ? 'mobile' : ''}`}
                            >
                              <span>Loading...</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="reporting-container">
      <div className="reporting-flex-container">


        {/* Main Content Container */}
        <div className={`reporting-main-content ${isMobile ? 'mobile' : sidebarCollapsed ? 'collapsed' : 'expanded'}`}>
          {/* Page Content */}
          <div className={getResponsiveClass('reporting-page-content', 'mobile', 'tablet')}>
            <div className="reporting-content-wrapper">
              {/* Page Header */}
              <div className={getResponsiveClass('reporting-page-header', 'mobile')}>
                <div className={getResponsiveClass('reporting-header-flex', 'mobile')}>
                  <FileText className={getResponsiveClass('reporting-header-icon', 'mobile')} />
                  <h1 className={getResponsiveClass('reporting-page-title', 'mobile', 'tablet')}>
                    Reporting Dashboard
                  </h1>
                </div>
                <p className={getResponsiveClass('reporting-page-description', 'mobile')}>
                  Export data, schedule deliveries, and manage your data transfer workflows
                </p>
              </div>

              {/* Analytics Navigation */}
              <div className="analytics-nav-container">
                <div className="analytics-nav-container-wrapper">
                  <button 
                    onClick={() => {
                      updateUserActivity(localStorage.getItem('email'));
                      navigate('/analytic?view=baseSuite');
                    }}
                    className="analytics-nav-btn"              
                  >
                    BASE SUITE
                  </button>
                  <button 
                    onClick={() => {
                      updateUserActivity(localStorage.getItem('email'));
                      navigate('/analytic?view=lprBaseSuite');
                    }}
                    className="analytics-nav-btn"              
                  >
                    LPR
                  </button>
                  <button 
                    onClick={() => {
                      updateUserActivity(localStorage.getItem('email'));
                      navigate('/analytic?view=lprInfringement');
                    }}
                    className="analytics-nav-btn"
                  >
                    LPR INFRINGEMENT
                  </button>
                  {/* REID */}
                  <button
                    onClick={() => {
                      updateUserActivity(localStorage.getItem('email'));
                      navigate('/analytic?view=reid');
                    }}
                    className="analytics-nav-btn"
                  >
                    REID
                  </button>
                  <button
                    onClick={() => {
                      navigate('/reporting');
                    }}
                    className="analytics-nav-btn"
                  >
                    REPORTING
                  </button>
                  <button
                    onClick={() => {
                      updateUserActivity(localStorage.getItem('email'));
                      navigate('/access');
                    }}
                    className="analytics-nav-btn"
                  >
                    ACCESS CONTROL
                  </button>
                </div>
              </div>

              {/* Feature Cards Grid */}
              <div className={getResponsiveClass('reporting-features-grid', 'mobile', 'tablet', 'desktop')}>
                {reportingFeatures.map((feature) => (
                  <div
                    key={feature.id}
                    className={`reporting-feature-card ${!isMobile ? '' : ''}`}
                    onClick={() => handleNavigation(feature.path)}
                  >
                    <div className={getResponsiveClass('reporting-feature-card-content', 'mobile')}>
                      {/* Icon and Title */}
                      <div className={getResponsiveClass('reporting-feature-header', 'mobile')}>
                        <div className={`reporting-feature-icon-wrapper ${feature.id} ${isMobile ? 'mobile' : ''}`}>
                          {React.cloneElement(feature.icon, { 
                            className: isMobile ? "w-6 h-6" : "w-8 h-8" 
                          })}
                        </div>
                        <div>
                          <h3 className={getResponsiveClass('reporting-feature-title', 'mobile')}>
                            {feature.title}
                          </h3>
                        </div>
                      </div>

                      {/* Description */}
                      <p className={getResponsiveClass('reporting-feature-description', 'mobile')}>
                        {feature.description}
                      </p>

                      {/* Features List */}
                      <div className="reporting-features-list-wrapper">
                        <ul className="reporting-features-list">
                          {feature.features.map((featureItem, index) => (
                            <li key={index} className={getResponsiveClass('reporting-feature-item', 'mobile')}>
                              <div className="reporting-feature-bullet"></div>
                              <span>{featureItem}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Action Button */}
                      <div className="reporting-action-button-wrapper">
                        <Button
                          variant="secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavigation(feature.path);
                          }}
                          className={`reporting-action-button ${feature.id} ${isMobile ? 'mobile' : ''}`}
                        >
                          <span>Open {feature.title}</span>
                          <svg className={getResponsiveClass('reporting-action-button-arrow', 'mobile')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportingPage;